import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { createSlice } from '@reduxjs/toolkit';
import { getSelectedBasemapFromLS } from 'actions/localStorage';
import { mapDrawStyles } from 'constants/mapConstants';
import DirectSelectModeOverride from 'utils/mapboxDrawUtils/DirectSelectModeOverride';
import SimpleSelectModeOverride from 'utils/mapboxDrawUtils/SimpleSelectModeOverride';
import DragCircleMode from 'utils/mapboxDrawUtils/DragCircleMode';

const computeInitialState = () => ({
	mapPinCoordinates: {
		lat: '',
		long: ''
	},
	notifyArea: null,
	refreshNotifyAndFlyLayer: false,
	drawInstance: new MapboxDraw({
		displayControlsDefault: false,
		userProperties: true,
		styles: mapDrawStyles,
		modes: {
			...MapboxDraw.modes,
			drag_circle: DragCircleMode,
			direct_select: DirectSelectModeOverride,
			simple_select: SimpleSelectModeOverride
		}
	}),
	radius: null,
	mapLoading: false,
	basemap: getSelectedBasemapFromLS(),
	mapLayers: [],
	visibleMapLayers: [],
	sources: [],
	mapRegions: [],
	mapBasemaps: [],
	mapModifiers: [],
	mapSections: [],
	geoJsonData: {}
});

const mapSlice = createSlice({
	name: 'map',
	initialState: computeInitialState(),
	reducers: {
		dropMapPin(state, action) {
			state.mapPinCoordinates = action.payload;
		},
		setNotifyArea(state, action) {
			state.notifyArea = action.payload;
		},
		setRefreshNotifyAndFlyLayer(state, action) {
			state.refreshNotifyAndFlyLayer = action.payload;
		},
		setRadius(state, action) {
			state.radius = action.payload;
		},
		setMapLoading(state, action) {
			state.mapLoading = action.payload;
		},
		setVisibleMapLayers(state, action) {
			state.visibleMapLayers = action.payload;
		},
		setMapLayers(state, action) {
			state.mapLayers = action.payload;
		},
		setTileServerSources(state, action) {
			state.tileServerSources = action.payload;
		},
		setMapSources(state, action) {
			state.sources = action.payload;
		},
		setMapRegions(state, action) {
			state.mapRegions = action.payload;
		},
		setMapBasemaps(state, action) {
			state.mapBasemaps = action.payload;
		},
		setSelectedBasemap(state, action) {
			state.basemap = action.payload;
		},
		setMapModifiers(state, action) {
			state.mapModifiers = action.payload;
		},
		setMapSections(state, action) {
			state.mapSections = action.payload;
		},
		setMapBoundingBox(state, action) {
			state.mapBoundingBox = action.payload;
		},
		setGeoJsonData(state, action) {
			state.geoJsonData = action.payload;
		},
		resetMapSlice() {
			return computeInitialState();
		}
	}
});

export const {
	dropMapPin,
	setNotifyArea,
	setRefreshNotifyAndFlyLayer,
	setRadius,
	setMapLoading,
	setVisibleMapLayers,
	setMapLayers,
	setTileServerSources,
	setMapSources,
	setMapRegions,
	setMapBasemaps,
	resetMapSlice,
	setSelectedBasemap,
	setMapModifiers,
	setMapSections,
	setMapBoundingBox,
	setGeoJsonData
} = mapSlice.actions;
export default mapSlice.reducer;

export const mapPinCoordinatesSelector = state => state.map.mapPinCoordinates;
export const notifyAreaSelector = state => state.map.notifyArea;
export const refreshNotifyAndFlyLayerSelector = state => state.map.refreshNotifyAndFlyLayer;
export const drawInstanceSelector = state => state.map.drawInstance;
export const radiusSelector = state => state.map.radius;
export const mapLoadingSelector = state => state.map.mapLoading;
export const visibleMapLayersSelector = state => state.map.visibleMapLayers;
export const mapLayersSelector = state => state.map.mapLayers;
export const mapSourcesSelector = state => state.map.sources;
export const mapRegionsSelector = state => state.map.mapRegions;
export const mapBasemapsSelector = state => state.map.mapBasemaps;
export const selectedBasemapSelector = state => state.map.basemap;
export const mapModifiersSelector = state => state.map.mapModifiers;
export const mapSectionsSelector = state => state.map.mapSections;
export const mapBoundingBoxSelector = state => state.map.mapBoundingBox;
export const geoJsonDataSelector = state => state.map.geoJsonData;
